/**
 * سربرگِ «شبیه‌سازِ تستر» — همان صفحه‌ای که هر تستر در qa.palsandplans.com
 * می‌بیند، با دیتای خودِ او، بی‌آنکه جدا با حسابِ او وارد شوی.
 *
 * صفحه‌ی تستر داخلِ یک iframe با `?viewAs=<user_id>` باز می‌شود. این صفحه
 * توکنِ نشستِ ادمین را با postMessage به آن می‌دهد (و با هر تازه‌شدنِ توکن
 * دوباره)، و صفحه‌ی تستر با همان توکن ردیف‌های آن تستر را می‌خواند — ادمین
 * طبقِ RLSِ 0161 همه‌ی ردیف‌های qa_* را می‌بیند، پس مایگریشنی لازم نیست.
 * ⚠ فقط‌خواندنی است و این را **صفحه‌ی تستر** تضمین می‌کند، نه این‌جا: در
 * حالتِ viewAs همه‌ی نوشتن‌هایش خاموش است (qa/index.html، «View-as mode»).
 * کلیک و جابه‌جایی داخلِ قاب فقط در همان قاب می‌ماند.
 */
import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qaRepo } from '@/core/data/qaRepo';
import { supabase } from '@/core/lib/supabase';
import { useUrlState } from '@/app/useUrlState';
import { QA_SITE } from '@/features/qa/QaTab';

export function QaViewTab() {
  const testers = useQuery({ queryKey: ['qa', 'testers'], queryFn: qaRepo.testers });
  const [picked, setPicked] = useUrlState('tester', '');
  const frame = useRef<HTMLIFrameElement>(null);
  const list = testers.data ?? [];
  const current = list.find((t) => t.user_id === picked) ?? list.find((t) => t.active) ?? list[0];

  useEffect(() => {
    const send = async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;
      frame.current?.contentWindow?.postMessage({
        type: 'qa-view-auth', token,
        url: import.meta.env.VITE_SUPABASE_URL, anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      }, QA_SITE);
    };
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin === QA_SITE && ev.data?.type === 'qa-view-ready') void send();
    };
    window.addEventListener('message', onMsg);
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED') void send();
    });
    return () => { window.removeEventListener('message', onMsg); sub.subscription.unsubscribe(); };
  }, []);

  return (
    <div>
      {testers.error ? <p className="err">{(testers.error as Error).message}</p> : null}
      <div className="card">
        <h2>شبیه‌سازِ تستر</h2>
        <p className="dim">
          همان چیزی که تستر در صفحه‌ی تست می‌بیند، با دیتای خودش. فقط نمایش است: هر کاری
          این‌جا بکنی ذخیره نمی‌شود و به تستر نمی‌رسد.
        </p>
        <div className="chiprow">
          {list.map((t) => (
            <button key={t.user_id} type="button" className={`chip${current?.user_id === t.user_id ? ' on' : ''}`}
              onClick={() => setPicked(t.user_id)} title={t.email}>
              {t.name || t.email}{t.active ? '' : ' (غیرفعال)'}
            </button>
          ))}
        </div>
        {!testers.isLoading && !list.length ? <p className="dim">هنوز تستری نیست.</p> : null}
      </div>
      {current ? (
        <iframe key={current.user_id} ref={frame} title={`QA · ${current.name || current.email}`}
          src={`${QA_SITE}/?viewAs=${encodeURIComponent(current.user_id)}`}
          style={{ width: '100%', height: 'calc(100vh - 24px)', border: '1px solid var(--line)', borderRadius: 8, display: 'block' }} />
      ) : null}
    </div>
  );
}
